import {Box, Text} from '@chakra-ui/layout';
import {useDisclosure} from '@chakra-ui/react';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {ItemCatalogue2StateFF, isItemStateCatalogueFF} from 'ff-common';
import {State} from '@synako/halitrek';
import {ItemCatalogue2Desc, LigneDePanierDesc} from 'api-types/ffconnect-hal';
import {HaliRenderAll, useHalState} from '@synako/haligator';
import {useClient} from '@/haligator-factory';
import {usePrixCategorieTag} from '@/hooks/usePrixCategorieTag';
import {TriSelection} from 'api-types/ffconnect';
import {useSimpleTextToast} from '@/hooks/useSimpleTextToast.tsx';
import {PrixBlockUnitaire} from '@/domains/cadencier/components/PrixBlockUnitaire.tsx';
import {useBuyingBlocks} from '@/hooks/panier/useBuyingBlocks.tsx';
import {useAjouterAvecPrixSaisi} from '@/hooks/panier/useAjouterAvecPrixSaisi';
import {ArticleBlock} from '@/domains/panier/PanierFound/ArticleBlock.tsx';
import {LigneProduitCommune} from '@/domains/ligneProduit/LigneProduitCommune';
import {SectionHeader} from '@/domains/ligneProduit/SectionHeader';
import {ProductSkeleton} from '@/backoffice/domains/PriseDeCommande/AjoutArticle/ProductSkeletonList';
import {useEditionPrixState} from '@/hooks/panier/usePriceEditor';

export const OperationCommercialeSection: React.FC<{
  title: string;
  id: string;
  articles: TriSelection[number]['articles'];
  lignesDePanier: State<LigneDePanierDesc>[];
}> = React.memo(({title, id, articles, lignesDePanier}) => {
  const {isOpen, onToggle} = useDisclosure();
  const clientNav = useClient();

  //Note(Nathan): J'ai peur que ça puisse créer beaucoup de 404
  //  pour les articles non presents au catalogue client
  const itemCatalogueNavs = articles.map(({code: code_article}) =>
    clientNav.follow('go:item_catalogue_2', {
      marketplace_id: 'FF',
      code: code_article,
    }),
  );

  // console.log('RERENDER : OperationCommercialeSection '+title);
  if (articles.length === 0) return null;

  return (
    <HaliRenderAll
      all={itemCatalogueNavs}
      fallback={() => null}
      render={({states: itemStates}) => {
        //On retire les articles en erreur
        const itemStatesFiltered = itemStates.filter(
          (itemState) => !!itemState,
        );
        if (itemStatesFiltered.length === 0) return null;
        return (
          <SectionHeader
            title={title}
            count={itemStatesFiltered.length}
            id={id}
            isOpen={isOpen}
            onToggle={onToggle}
          >
            <Box
              marginBottom='1px'
              px='12px'
            >
              {itemStatesFiltered.map((itemState) => {
                const code_article = itemState.data.code;
                const ligneDePanier = lignesDePanier.find(
                  (l) => l.data.code_article === code_article,
                );
                return (
                  <LigneOperationCommerciale
                    key={code_article}
                    ligneDePanier={ligneDePanier}
                    itemState={itemState}
                  />
                );
              })}
            </Box>
          </SectionHeader>
        );
      }}
    />
  );
});

const LigneOperationCommerciale: React.FC<{
  itemState: State<ItemCatalogue2Desc>;
  ligneDePanier: State<LigneDePanierDesc> | undefined;
}> = React.memo((props) => {
  const {itemState, ...rest} = props;
  if (!isItemStateCatalogueFF(itemState)) return null;
  return (
    <LigneOperationCommercialeContent
      {...rest}
      itemState={itemState}
    />
  );
});

const LigneOperationCommercialeContent: React.FC<{
  itemState: ItemCatalogue2StateFF;
  ligneDePanier: State<LigneDePanierDesc> | undefined;
}> = React.memo(({itemState, ligneDePanier}) => {
  const {t} = useTranslation();
  const textToast = useSimpleTextToast();
  const {state: articleState, loading: articleLoading} = useHalState(
    itemState.follow('article'),
  );
  const code_article = itemState.data.code;

  const precommande = itemState.data.tolede.precommande;
  // Hors panier : la catégorie vient du catalogue, pas de la ligne. Celle-ci
  // n'est passée que pour une info : un prix saisi ou bloqué par le commercial,
  // qui prend alors la place du tag de catégorie.
  const {categorieDeTarif, modeTarifaireSouhaite} = usePrixCategorieTag(
    ligneDePanier,
    itemState.data.tolede.prix_par_livraison?.[0]?.categorie,
    {contexte: 'catalogue'},
  );
  const {uniteQuantiteBlock, buyBlock, commandeEnColis} = useBuyingBlocks(
    textToast,
    'operation_commerciale',
    itemState,
    ligneDePanier,
  );
  const {
    ajouterAvecPrixSaisi,
    isAddingAvecPrix,
    saisieAvantAjout,
    codeArticle,
  } = useAjouterAvecPrixSaisi(itemState, commandeEnColis);
  const {prixDraft, onEditionPrixChange} = useEditionPrixState();
  const prixBlock = (
    <PrixBlockUnitaire
      prixUnitaire={itemState.data.prix_unitaire_ht}
      prixUnitaireTtc={itemState.data.prix_unitaire_ttc}
      uniteFacturation={itemState.data.unite_de_facturation}
      tauxTVA={itemState.data.tva}
      typePrix={itemState.data.tolede.prix_par_livraison![0].type}
      categorieDeTarif={categorieDeTarif}
      modeTarifaireSouhaite={modeTarifaireSouhaite}
      ligneDePanier={ligneDePanier}
      saisieAvantAjout={saisieAvantAjout}
      ajouterAvecPrixSaisi={ajouterAvecPrixSaisi}
      codeArticle={codeArticle}
      onEditionPrixChange={onEditionPrixChange}
    />
  );

  if (articleLoading) {
    return <ProductSkeleton />;
  }
  if (!articleState) {
    return (
      <Text>
        {t('operation_commerciale.article_hors_base', {code_article})}
      </Text>
    );
  }

  const articleBlock = (
    <ArticleBlock
      code_article={code_article}
      libelles_tronques
      ligneDePanier={ligneDePanier}
      afficherTagCadencier
      prixDraft={prixDraft}
    />
  );

  return (
    <LigneProduitCommune
      itemState={itemState}
      articleBlock={articleBlock}
      prixBlock={prixBlock}
      uniteQuantiteBlock={uniteQuantiteBlock}
      aPeser={articleState.data.tolede.a_peser}
      buyBlock={buyBlock}
      somethingSubmitting={isAddingAvecPrix}
      precommande={precommande}
      ligneDePanier={ligneDePanier}
      afficher_suggestions
    />
  );
});
